import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import Typography from "@mui/material/Typography";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Button,
  useMediaQuery,
  useTheme,
  Fade,
  CircularProgress,
} from "@mui/material";
import { Email, Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import talentIq from "../../assets/images/Talent_IQ_IMG_Trans.png";
import { useState, useEffect } from "react";
import { useSnackbar } from "../../providers/SnackbarProvider";
import api from "../../api/client";
import { useDispatch } from "react-redux";
import { setChangeableItems, setAllChangeableItems, setSelectedOrganization } from "../../store/changeableItemsSlice";

// Background images - replace with your actual images
const backgroundImages = [
  "https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80",
  "https://images.unsplash.com/photo-1501504905252-473c47e087f8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80",
  "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
  "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
];

// Validation schema
const schema = Yup.object().shape({
  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: Yup.string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters"),
});

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const loginParah = [
    "An AI-powered platform that helps students and trainers assess skills and deliver personalized training across diverse domains",
  ];
  const theme1 = useTheme();
  const isSmallScreen = useMediaQuery(theme1.breakpoints.down("md"));

  // Background image carousel effect
  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setCurrentBgIndex((prev) => (prev + 1) % backgroundImages.length);
        setFadeIn(true);
      }, 200);
    }, 8000); // Change image every 5 seconds

    // Trigger initial animation
    setTimeout(() => setIsLoaded(true), 100);

    return () => clearInterval(interval);
  }, []);

  // Surface a session-expiry (401) notice handed over by the api interceptor.
  useEffect(() => {
    const msg = sessionStorage.getItem("authMessage");
    if (msg) {
      showSnackbar("error", msg);
      sessionStorage.removeItem("authMessage");
    }
  }, [showSnackbar]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        email: data?.email || "",
        password: data?.password || "",
      };
      const res = await api.post("Login", payload);
      if (res?.data?.data) {
        localStorage.setItem("userData", JSON.stringify(res.data.data));
        localStorage.setItem("token", res?.data?.data?.access_token);
        localStorage.setItem(
          "UserName",
          res?.data?.data?.first_name + " " + res?.data?.data?.last_name
        );
        localStorage.setItem("email", res?.data?.data?.email);
        localStorage.setItem("CollegeName", res?.data?.data?.institute_name);
        localStorage.setItem("CollegeShortName", res?.data?.data?.short_name);
        localStorage.setItem("firstName", res?.data?.data?.first_name);
        localStorage.setItem("lastName", res?.data?.data?.last_name);
        localStorage.setItem("role", res?.data?.data?.role);
        localStorage.setItem("InstituteId", res?.data?.data?.institute_id);
        const allChangeableItems = res?.data?.data?.changeable_items || [];
        const role = res?.data?.data?.role;
        
        dispatch(setAllChangeableItems(allChangeableItems));
        
        if (role === "SUPER_ADMIN") {
          dispatch(setChangeableItems({
            changeableItems: allChangeableItems,
            organizationName: res?.data?.data?.organization_name || null
          }));
          // Auto-select the first unique organization as the default so the
          // sidebar dropdown has a value pre-populated on login.
          const orgMap = new Map();
          allChangeableItems.forEach(item => {
            if (item.organization_id && item.organization_name) {
              orgMap.set(item.organization_id, item.organization_name);
            }
          });
          const firstOrgId = orgMap.size > 0 ? Array.from(orgMap.keys())[0] : null;
          if (firstOrgId) {
            dispatch(setSelectedOrganization(firstOrgId));
          }
        } else {
          const userOrgId = res?.data?.data?.organization_id;
          const filteredItems = userOrgId 
            ? allChangeableItems.filter(item => item.organization_id === userOrgId)
            : allChangeableItems;
          dispatch(setChangeableItems({
            changeableItems: filteredItems,
            organizationName: res?.data?.data?.organization_name || null
          }));
        }
        
        localStorage.removeItem("menu");
        setTimeout(() => {
          localStorage.setItem("logIn", "true");
        }, 1000);

        if (
          localStorage.getItem("role") === "SUPER_ADMIN" ||
          localStorage.getItem("role") === "INSTITUTE_ADMIN"
        ) {
          navigate("/dashboard");
        } else if (localStorage.getItem("role") === "TRAINER") {
          navigate("/dashboard");
        } else if (localStorage.getItem("role") === "STUDENT") {
          navigate("/dashboard");
        }
        showSnackbar("success", res?.data?.message);
      } else {
        showSnackbar("error", res?.data?.message);
      }
    } catch (error) {
      showSnackbar("error", "Login failed. Please check your credentials and try again.");
      console.log("Error :", error);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        background: "black",
      }}
    >
      {/* Background Image Carousel */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        <Fade in={fadeIn} timeout={800}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.7)), url(${backgroundImages[currentBgIndex]})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              position: "absolute",
            }}
          />
        </Fade>

        {/* Gradient overlay for better text readability */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            // background:
            //   "linear-gradient(135deg, rgba(25, 118, 210, 0.9) 0%, rgba(25, 118, 210, 0.6) 100%)",
            zIndex: 1,
          }}
        />
      </Box>

      {/* Content */}
      <Grid
        container
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          minHeight: "100vh",
          px: { xs: 2, md: 8 },
          py: 4,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        {/* Left Side - Branding & Info */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            color: "white",
            pr: { md: 4 },
            minHeight: "100vh",
            transform: isLoaded ? "translateX(0)" : "translateX(-50px)",
            opacity: isLoaded ? 1 : 0,
            transition: "all 0.8s ease-out",
            transitionDelay: "0.2s",
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 6 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                transform: isLoaded ? "translateY(0)" : "translateY(-20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.4s",
              }}
            >
              {/* <img
                src={talentIq}
                alt="Logo"
                style={{
                  width: "120px",
                  height: "60px",
                  filter: "brightness(0) invert(1)",
                  transform: isLoaded ? "scale(1)" : "scale(0.8)",
                  transition: "transform 0.6s ease-out",
                }}
                draggable={false}
              /> */}
              <Typography
                sx={{
                  fontSize: "32px",
                  fontWeight: "bold",
                  color: "white",
                  // ml: 2,
                  transform: isLoaded ? "translateX(0)" : "translateX(-10px)",
                  opacity: isLoaded ? 1 : 0,
                  transition: "all 0.6s ease-out",
                  transitionDelay: "0.5s",
                }}
              >
                Talent IQ
              </Typography>
            </Box>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.8)",
                fontWeight: 400,
                transform: isLoaded ? "translateY(0)" : "translateY(10px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.6s",
              }}
            >
              Professional Assessment Platform
            </Typography>
          </Box>

          {/* Main Heading */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontSize: { xs: "40px", md: "52px" },
                fontWeight: "bold",
                lineHeight: 1.1,
                mb: 1,
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.7s ease-out",
                transitionDelay: "0.3s",
              }}
            >
              Assess Your
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: "40px", md: "52px" },
                fontWeight: "bold",
                color: "#62a7f8",
                lineHeight: 1.1,
                mb: 3,
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.7s ease-out",
                transitionDelay: "0.4s",
              }}
            >
              Potential
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.9)",
                fontSize: "18px",
                lineHeight: 1.6,
                maxWidth: "500px",
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.7s ease-out",
                transitionDelay: "0.5s",
              }}
            >
              {loginParah}
            </Typography>
          </Box>

          {/* Stats */}
          {/* <Box
            sx={{
              display: "flex",
              gap: { xs: 3, md: 6 },
              flexWrap: "wrap",
              transform: isLoaded ? "translateY(0)" : "translateY(30px)",
              opacity: isLoaded ? 1 : 0,
              transition: "all 0.8s ease-out",
              transitionDelay: "0.6s",
            }}
          >
            <Box
              sx={{
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.7s",
              }}
            >
              <Typography
                sx={{ fontSize: "28px", fontWeight: "bold", color: "#62a7f8" }}
              >
                50K+
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                Tests Completed
              </Typography>
            </Box>
            <Box
              sx={{
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.8s",
              }}
            >
              <Typography
                sx={{ fontSize: "28px", fontWeight: "bold", color: "#62a7f8" }}
              >
                95%
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                Success Rate
              </Typography>
            </Box>
            <Box
              sx={{
                transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                opacity: isLoaded ? 1 : 0,
                transition: "all 0.6s ease-out",
                transitionDelay: "0.9s",
              }}
            >
              <Typography
                sx={{ fontSize: "28px", fontWeight: "bold", color: "#62a7f8" }}
              >
                24/7
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                Support
              </Typography>
            </Box>
          </Box> */}
        </Grid>

        {/* Right Side - Login Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            transform: isLoaded ? "translateX(0)" : "translateX(50px)",
            opacity: isLoaded ? 1 : 0,
            transition: "all 0.8s ease-out",
            transitionDelay: "0.3s",
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: "16px",
              p: { xs: 3, md: 5 },
              width: "100%",
              maxWidth: "450px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              transform: isLoaded
                ? "translateY(0) scale(1)"
                : "translateY(30px) scale(0.95)",
              opacity: isLoaded ? 1 : 0,
              transition: "all 0.6s ease-out",
              transitionDelay: "0.5s",
            }}
          >
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  sx={{
                    fontSize: "28px",
                    fontWeight: 600,
                    color: "#2b4660",
                    mb: 1,
                    transform: isLoaded ? "translateY(0)" : "translateY(-10px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "0.7s",
                  }}
                >
                  Welcome Back
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#64748b",
                    transform: isLoaded ? "translateY(0)" : "translateY(-10px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "0.8s",
                  }}
                >
                  Please enter your credentials to access your account
                </Typography>
              </Box>

              {/* Email Field */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    transform: isLoaded ? "translateX(0)" : "translateX(-20px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "0.9s",
                  }}
                >
                  <Email sx={{ color: "#64748b", fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 500, color: "#2b4660" }}>
                    Email Address <span style={{ color: "red" }}>*</span>
                  </Typography>
                </Box>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="medium"
                      placeholder="Enter your email"
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          "&:hover fieldset": {
                            borderColor: "#62a7f8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#62a7f8",
                            borderWidth: "2px",
                          },
                          transform: isLoaded
                            ? "translateY(0)"
                            : "translateY(10px)",
                          opacity: isLoaded ? 1 : 0,
                          transition: "all 0.5s ease-out",
                          transitionDelay: "1s",
                        },
                      }}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Box>

              {/* Password Field */}
              <Box sx={{ mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    transform: isLoaded ? "translateX(0)" : "translateX(-20px)",
                    opacity: isLoaded ? 1 : 0,
                    transition: "all 0.5s ease-out",
                    transitionDelay: "1.1s",
                  }}
                >
                  <Lock sx={{ color: "#64748b", fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 500, color: "#2b4660" }}>
                    Password <span style={{ color: "red" }}>*</span>
                  </Typography>
                </Box>
                <Controller
                  name="password"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type={showPassword ? "text" : "password"}
                      size="medium"
                      placeholder="Enter your password"
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "white",
                          "&:hover fieldset": {
                            borderColor: "#62a7f8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#62a7f8",
                            borderWidth: "2px",
                          },
                          transform: isLoaded
                            ? "translateY(0)"
                            : "translateY(10px)",
                          opacity: isLoaded ? 1 : 0,
                          transition: "all 0.5s ease-out",
                          transitionDelay: "1.2s",
                        },
                      }}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showPassword ? "hide password" : "show password"
                              }
                              onClick={handleClickShowPassword}
                              edge="end"
                              sx={{ color: "#64748b" }}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {/* Forgot Password */}
              <Typography
                onClick={handleForgotPassword}
                sx={{
                  color: "#62a7f8",
                  textAlign: "right",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                  mb: 3,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  transform: isLoaded ? "translateY(0)" : "translateY(10px)",
                  opacity: isLoaded ? 1 : 0,
                  transition: "all 0.5s ease-out",
                  transitionDelay: "1.3s",
                }}
              >
                Forgot your password?
              </Typography>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                className="gradient-button"
                fullWidth
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={18} color="inherit" /> : null
                }
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Login;
